import { TabContext } from '@mui/lab';
import { CssBaseline } from '@mui/material';
import { LicenseInfo } from '@mui/x-data-grid-pro';
import { SnackbarProvider } from 'notistack';
import { lazy, ReactElement, Suspense, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { parseCommaSeparatedTags, RoleType, setupSentry } from 'utils';
import Banner from './components/Banner';
import { CommandPalette } from './components/CommandPalette';
import { CommandPaletteRegistrations } from './components/CommandPaletteRegistrations';
import LogoutWarning from './components/dialogs/LogoutWarning';
import { LoadingScreen } from './components/LoadingScreen';
import Navbar from './components/navigation/Navbar';
import { ProtectedRoute } from './components/routing/ProtectedRoute';
import { TestErrorPage } from './components/TestErrorPage';
import { FEATURE_FLAGS } from './constants/feature-flags';
import { CustomThemeProvider } from './CustomThemeProvider';
import { UnsolicitedResultsInbox } from './features/external-labs/pages/UnsolicitedResultsInbox';
import { UnsolicitedResultsMatch } from './features/external-labs/pages/UnsolicitedResultsMatch';
import { UnsolicitedResultsReview } from './features/external-labs/pages/UnsolicitedResultsReview';
import { Tasks } from './features/tasks/pages/Tasks';
import AddPatientFollowup from './features/visits/shared/components/patient/AddPatientFollowup';
import PatientFollowup from './features/visits/shared/components/patient/PatientFollowup';
import EditChargeItem from './features/visits/telemed/components/admin/EditChargeItem';
import EditInsurance from './features/visits/telemed/components/admin/EditInsurance';
import EditVirtualLocationPage from './features/visits/telemed/components/admin/EditVirtualLocationPage';
import GlobalTemplateDetailPage from './features/visits/telemed/components/admin/GlobalTemplateDetailPage';
import ImmunizationQuickPickDetailPage from './features/visits/telemed/components/admin/ImmunizationQuickPickDetailPage';
import AdminAddInHouseLab from './features/visits/telemed/components/admin/in-house-labs/AdminAddInHouseLab';
import AdminInHouseLabDetails from './features/visits/telemed/components/admin/in-house-labs/AdminInHouseLabDetails';
import InHouseMedicationQuickPickDetailPage from './features/visits/telemed/components/admin/InHouseMedicationQuickPickDetailPage';
import ProcedureQuickPickDetailPage from './features/visits/telemed/components/admin/ProcedureQuickPickDetailPage';
import RadiologyQuickPickDetailPage from './features/visits/telemed/components/admin/RadiologyQuickPickDetailPage';
import { useApiClients } from './hooks/useAppClients';
import useEvolveUser from './hooks/useEvolveUser';
import AddEmployeePage from './pages/AddEmployeePage';
import AddPatient from './pages/AddPatient';
import AddSchedulePage from './pages/AddSchedulePage';
import { AdminPage } from './pages/AdminPage';
import AppointmentsPage from './pages/Appointments';
import AddMedicationPage from './pages/configuration/AddMedicationPage';
import UpdateMedicationPage from './pages/configuration/UpdateMedicationPage';
import EditEmployeePage from './pages/EditEmployee';
import EmployeeProfilePage from './pages/EmployeeProfilePage';
import GroupPage from './pages/GroupPage';
import LegacyDataPage from './pages/LegacyDataPage';
import Logout from './pages/Logout';
import PatientDocumentsExplorerPage from './pages/PatientDocumentsExplorerPage';
import PatientInformationPage from './pages/PatientInformationPage';
import PatientPage from './pages/PatientPage';
import PatientsPage from './pages/Patients';
import PaymentLocationDetailPage from './pages/PaymentLocationDetailPage';
import Reports from './pages/Reports';
import {
  AiAssistedEncounters,
  CompleteEncounters,
  DailyPayments,
  DataExports,
  IncompleteEncounters,
  InvoiceablePatients,
  PracticeKpis,
  RecentPatients,
  VisitsOverview,
} from './pages/reports/index';
import SchedulePage from './pages/SchedulePage';
import TaskAdmin from './pages/TaskAdmin';
import VisitDetailsPage from './pages/VisitDetailsPage';
import { Claim, Claims } from './rcm';
import { useNavStore } from './state/nav.store';

const { VITE_APP_SENTRY_DSN, VITE_APP_SENTRY_ENV, VITE_APP_SENTRY_TAGS } = import.meta.env;

setupSentry({
  dsn: VITE_APP_SENTRY_DSN,
  environment: VITE_APP_SENTRY_ENV,
  tags: parseCommaSeparatedTags(VITE_APP_SENTRY_TAGS),
});

const InPersonRoutingLazy = lazy(() => import('./features/visits/in-person/routing/InPersonRouting'));

export const INSURANCES_URL = '/admin/insurances';
export const FEE_SCHEDULES_URL = '/admin/fee-schedule';
export const CHARGE_MASTERS_URL = '/admin/charge-masters';
export const VIRTUAL_LOCATIONS_URL = '/admin/virtual-locations';
export const BILLING_URL = '/admin/billing';
export const PAYMENT_LOCATIONS_URL = '/admin/billing/payments/locations';
export const GLOBAL_TEMPLATES_URL = '/admin/global-templates';

const MUI_X_LICENSE_KEY = import.meta.env.VITE_APP_MUI_X_LICENSE_KEY;
if (MUI_X_LICENSE_KEY != null) {
  LicenseInfo.setLicenseKey(MUI_X_LICENSE_KEY);
}

export const showEnvironmentBanner = import.meta.env.VITE_APP_ENV !== 'production';

function App(): ReactElement {
  useApiClients();
  const currentUser = useEvolveUser();
  const currentTab = useNavStore((state) => state.currentTab) || 'In Person';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);

  const handleOnIdle = (): void => {
    window.location.href = '/logout';
  };

  const handleOnPrompt = (): void => {
    setIsModalOpen(true);
    setTimeLeft(Math.ceil(getRemainingTime() / 1000));
  };

  const handleContinue = (): void => {
    setIsModalOpen(false);
    reset();
  };

  const handleEndSession = (): void => {
    setIsModalOpen(false);
    handleOnIdle();
  };

  const { reset, getRemainingTime } = useIdleTimer({
    timeout: 60 * 60 * 1000, // 60 minutes
    onIdle: handleOnIdle,
    onPrompt: handleOnPrompt,
    promptBeforeIdle: 1 * 60 * 1000, // 60 second warning
    debounce: 500,
  });

  const roleUnknown =
    !currentUser ||
    !currentUser.hasRole([
      RoleType.Administrator,
      RoleType.Staff,
      RoleType.Manager,
      RoleType.Provider,
      RoleType.CustomerSupport,
    ]);

  return (
    <CustomThemeProvider>
      <CssBaseline />
      <LogoutWarning
        modalOpen={isModalOpen}
        onEnd={handleEndSession}
        onContinue={handleContinue}
        timeoutInSeconds={timeLeft}
      />
      {showEnvironmentBanner && (
        <>
          <Banner
            text={`${import.meta.env.VITE_APP_ENV?.toUpperCase()} environment`}
            icon="warning"
            iconSize="medium"
            bgcolor="info.main"
            color="info.contrast"
          />
        </>
      )}
      <BrowserRouter>
        <Routes>
          <Route
            path="/in-person/:id/*"
            element={
              <ProtectedRoute
                showWhenAuthenticated={
                  <Suspense fallback={<LoadingScreen />}>
                    <InPersonRoutingLazy />
                  </Suspense>
                }
              />
            }
          />
          <Route
            element={
              <TabContext value={currentTab}>
                <Navbar />
                <ProtectedRoute
                  showWhenAuthenticated={
                    <>
                      <Outlet />
                    </>
                  }
                />
              </TabContext>
            }
          >
            {roleUnknown && (
              <>
                <Route path="/logout" element={<Logout />} />
                <Route path="*" element={<LoadingScreen />} />
              </>
            )}
            {currentUser?.hasRole([RoleType.Administrator, RoleType.CustomerSupport]) && (
              <>
                <Route path="/tasks-observability" element={<TaskAdmin />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/reports/incomplete-encounters" element={<IncompleteEncounters />} />
                <Route path="/reports/complete-encounters" element={<CompleteEncounters />} />
                <Route path="/reports/ai-assisted-encounters" element={<AiAssistedEncounters />} />
                <Route path="/reports/daily-payments" element={<DailyPayments />} />
                <Route path="/reports/practice-kpis" element={<PracticeKpis />} />
                <Route path="/reports/data-exports" element={<DataExports />} />
                <Route path="/reports/visits-overview" element={<VisitsOverview />} />
                <Route path="/reports/recent-patients" element={<RecentPatients />} />
                <Route path="/reports/invoiceable-patients" element={<InvoiceablePatients />} />
              </>
            )}
            {currentUser?.hasRole([RoleType.Administrator, RoleType.Manager, RoleType.CustomerSupport]) && (
              <>
                <Route path="/" element={<Navigate to="/visits" />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/visits" element={<AppointmentsPage />} />
                <Route path="/visits/add" element={<AddPatient />} />
                <Route path="/visit/:id" element={<VisitDetailsPage />} />
                <Route path="/profile" element={<EmployeeProfilePage />} />
                <Route path="/patients" element={<PatientsPage />} />
                <Route path="/patient/:id" element={<PatientPage />} />
                <Route path="/patient/:id/info" element={<PatientInformationPage />} />
                <Route path="/patient/:id/docs" element={<PatientDocumentsExplorerPage />} />
                <Route path="/patient/:id/followup/add" element={<AddPatientFollowup />} />
                {FEATURE_FLAGS.LEGACY_PATIENT_FOLLOWUPS_ENABLED && (
                  <Route path="/patient/:id/followup/:encounterId" element={<PatientFollowup />} />
                )}
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/billing/:billingTab" element={<AdminPage />} />
                <Route path="/admin/:adminTab" element={<AdminPage />} />
                <Route path="/admin/quick-picks/procedure/:quickPickId" element={<ProcedureQuickPickDetailPage />} />
                <Route path="/admin/quick-picks/radiology/:quickPickId" element={<RadiologyQuickPickDetailPage />} />
                <Route
                  path="/admin/quick-picks/immunization/:quickPickId"
                  element={<ImmunizationQuickPickDetailPage />}
                />
                <Route
                  path="/admin/quick-picks/in-house-medication/:quickPickId"
                  element={<InHouseMedicationQuickPickDetailPage />}
                />
                <Route path="/admin/employees/add" element={<AddEmployeePage />} />
                <Route path="/admin/employee/:id" element={<EditEmployeePage />} />
                <Route path="/admin/schedule/:schedule-type/add" element={<AddSchedulePage />} />
                <Route path="/admin/group/id/:group-id" element={<GroupPage />} />
                <Route path="/admin/schedule/id/:schedule-id" element={<SchedulePage />} />
                <Route path="/admin/schedule/new/:schedule-type/:owner-id" element={<SchedulePage />} />
                <Route path="/admin/medications/add" element={<AddMedicationPage />} />
                <Route path="/admin/medication/:medication-id" element={<UpdateMedicationPage />} />
                <Route path={`${VIRTUAL_LOCATIONS_URL}/:id`} element={<EditVirtualLocationPage />} />
                <Route path={`${INSURANCES_URL}/:insurance`} element={<EditInsurance />} />
                <Route path={`${FEE_SCHEDULES_URL}/:id`} element={<EditChargeItem />} />
                <Route path={`${CHARGE_MASTERS_URL}/:id`} element={<EditChargeItem mode="charge-master" />} />
                <Route path={`${PAYMENT_LOCATIONS_URL}/:id`} element={<PaymentLocationDetailPage />} />
                <Route path={`${GLOBAL_TEMPLATES_URL}/:templateId`} element={<GlobalTemplateDetailPage />} />
                <Route path="/admin/in-house-labs/add" element={<AdminAddInHouseLab />} />
                <Route path="/admin/in-house-labs/:activityDefinitionId" element={<AdminInHouseLabDetails />} />
                {FEATURE_FLAGS.LEGACY_DATA_ENABLED && <Route path="/legacy-data" element={<LegacyDataPage />} />}
                <Route path="/tasks" element={<Tasks />} />
                <Route path="*" element={<Navigate to={'/'} />} />
              </>
            )}
            {currentUser?.hasRole([RoleType.Staff, RoleType.Provider, RoleType.CustomerSupport]) && (
              <>
                <Route path="/" element={<Navigate to="/visits" />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/visits" element={<AppointmentsPage />} />
                <Route path="/visits/add" element={<AddPatient />} />
                <Route path="/visit/:id" element={<VisitDetailsPage />} />
                <Route path="/profile" element={<EmployeeProfilePage />} />
                <Route path="/patient/:id" element={<PatientPage />} />
                <Route path="/patient/:id/info" element={<PatientInformationPage />} />
                <Route path="/patient/:id/docs" element={<PatientDocumentsExplorerPage />} />
                <Route path="/patient/:id/followup/add" element={<AddPatientFollowup />} />
                {FEATURE_FLAGS.LEGACY_PATIENT_FOLLOWUPS_ENABLED && (
                  <Route path="/patient/:id/followup/:encounterId" element={<PatientFollowup />} />
                )}
                <Route path="/patients" element={<PatientsPage />} />

                <Route path="/unsolicited-results" element={<UnsolicitedResultsInbox />} />
                <Route path="/unsolicited-results/:diagnosticReportId/match" element={<UnsolicitedResultsMatch />} />
                <Route path="/unsolicited-results/:diagnosticReportId/review" element={<UnsolicitedResultsReview />} />

                <Route path="/rcm/claims" element={<Claims />} />
                <Route path="/rcm/claims/:id" element={<Claim />} />
                {FEATURE_FLAGS.LEGACY_DATA_ENABLED && <Route path="/legacy-data" element={<LegacyDataPage />} />}
                <Route path="/tasks" element={<Tasks />} />
                <Route path="*" element={<Navigate to={'/'} />} />
              </>
            )}
          </Route>
          <Route path="/test-error" element={<TestErrorPage />} />
        </Routes>
        <CommandPaletteRegistrations />
        <CommandPalette />
        <SnackbarProvider maxSnack={5} autoHideDuration={6000} />
      </BrowserRouter>
    </CustomThemeProvider>
  );
}

export default App;
